import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { COUNTRIES, getCountryCodeByName } from "@/constants/countries";
import { useTranslation } from "react-i18next";

// Common phone number patterns by country
export const getPhoneMask = (country: string) => {
  const masks = {
    "Brasil": "(99) 99999-9999",
    "Estados Unidos": "(999) 999-9999",
    "Canadá": "(999) 999-9999",
    "Portugal": "999 999 999",
    "Espanha": "999 999 999",
    "França": "99 99 99 99 99",
    "Alemanha": "999 999999",
    "Itália": "999 999 9999",
    "Reino Unido": "99999 999999",
    "Argentina": "999 999-9999",
    "México": "999 999 9999",
    "Chile": "9999 9999",
    "Colômbia": "999 999 9999",
    "Peru": "999 999 999",
    "Japão": "999-9999-9999",
    "China": "999 9999 9999",
    "Coreia do Sul": "999-9999-9999",
    "Índia": "99999 99999",
    "Austrália": "9999 999 999",
    "Nova Zelândia": "999 999 9999"
  };

  return masks[country as keyof typeof masks] || "999 999 9999";
};

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  country: string;
  onCountryChange: (country: string) => void;
  label?: string;
  placeholder?: string;
  className?: string;
  required?: boolean;
}

const PhoneInput = ({
  value,
  onChange,
  country,
  onCountryChange,
  label,
  placeholder,
  className = "",
  required = false
}: PhoneInputProps) => {
  const [openCountry, setOpenCountry] = useState(false);
  const { t } = useTranslation();
  const displayLabel = label || t('phoneInput.label');

  const formatPhoneWithMask = (value: string, country: string) => {
    const digits = value.replace(/\D/g, '');
    const mask = getPhoneMask(country);
    let result = '';
    let digitIndex = 0;

    for (let i = 0; i < mask.length && digitIndex < digits.length; i++) {
      if (mask[i] === '9') {
        result += digits[digitIndex];
        digitIndex++;
      } else {
        result += mask[i];
      }
    }

    return result;
  };

  const handlePhoneChange = (value: string) => {
    const formattedValue = formatPhoneWithMask(value, country);
    onChange(formattedValue);
  };

  const handleCountryChange = (newCountry: string) => {
    onCountryChange(newCountry);
    // Clear phone when country changes
    onChange("");
    setOpenCountry(false);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <Label htmlFor="phone" className="text-white">
        {displayLabel}
      </Label>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Popover open={openCountry} onOpenChange={setOpenCountry}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={openCountry}
                className="w-full justify-between bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
              >
                {country === "outro"
                  ? t('phoneInput.other')
                  : country
                    ? `${COUNTRIES.find(c => c.name === country)?.flag || ""} ${country} (${getCountryCodeByName(country)})`
                    : t('phoneInput.selectCountry')
                }
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0 bg-gray-700 border-gray-600">
              <Command className="bg-gray-700">
                <CommandInput
                  placeholder={t('phoneInput.searchCountry')}
                  className="text-white placeholder:text-gray-400"
                />
                <CommandList className="max-h-[200px]">
                  <CommandEmpty className="text-gray-400">{t('phoneInput.noCountryFound')}</CommandEmpty>
                  <CommandGroup>
                    {COUNTRIES.map((countryItem) => (
                      <CommandItem
                        key={countryItem.code}
                        value={`${countryItem.name} ${countryItem.flag} ${countryItem.phoneCode}`}
                        onSelect={() => handleCountryChange(countryItem.name)}
                        className="text-white hover:bg-gray-600"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            country === countryItem.name ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {countryItem.flag} {countryItem.name} ({countryItem.phoneCode})
                      </CommandItem>
                    ))}
                    <CommandItem
                      value="outro"
                      onSelect={() => handleCountryChange("outro")}
                      className="text-white hover:bg-gray-600"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          country === "outro" ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {t('phoneInput.other')}
                    </CommandItem>
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <div className="relative">
            <div className="pointer-events-none absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
              {country !== "outro" ? getCountryCodeByName(country) : "+"}
            </div>
            <Input
              id="phone"
              type="tel"
              placeholder={country !== "outro" ? getPhoneMask(country).replace(/9/g, '0') : placeholder || "(99) 999999999"}
              value={value}
              onChange={(e) => handlePhoneChange(e.target.value)}
              className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 pl-12"
              required={required}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneInput;
